import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import { CountryLookback, Location, LookBackYearRange, Product, ProductClass } from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { generateStringProductId } from '../../graphQL/Utils';
import ExportGrowthDynamicsText from './ExportGrowthDynamicsText';
import {
  ContentContainer,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

const query = gql`
  query ExportGrowthComplexitySideBar($id: ID!, $yearRange: LookBackYearRange!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryLookback(id: $id, yearRange: $yearRange) {
      id
      exportValueGrowthClassification
      largestContributingExportProduct {
        id
        shortName
        topLevelParent {
          id
        }
      }
    }
  }
`;

export interface SuccessResponse {
  location: null | {
    id: Location['id']
    shortName: Location['shortName']
    nameAbbr: Location['nameAbbr']
    thePrefix: Location['thePrefix'],
  };
  countryLookback: null | {
    largestContributingExportProduct: Array<{
      id: Product['id']
      topLevelParent: {
        id: Product['topLevelParent']['id'],
      }
      shortName: Product['shortName'],
    }>,
    exportValueGrowthClassification: CountryLookback['exportValueGrowthClassification'],
  };
}

interface Variables {
  id: string;
  yearRange: LookBackYearRange;
}

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  countryId: string;
  fluentBundle: FluentBundle;
  year: number;
  yearRange: LookBackYearRange;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

export default (props: Props) => {
  const {
    onNextSlideClick, onPrevSlideClick, getFluentString,
    countryId, fluentBundle, year, yearRange, isHelpActive, helpTooltipIndex,
  } = props;

  const variables: Variables = {
    id: countryId,
    yearRange,
  };

  /*
  KNOWN BUG IN APOLLO CLIENT FOR REACT:

  The data from the following query are returned correctly on first request,
  but in subsequent requests, get return result of:
    loading: false
    error: undefined
    data: undefined

  This causes the text content for this slide to appear and disappear arbitrarily.

  This seems to be a known bug in apollo client:

  https://github.com/apollographql/react-apollo/issues/1314

  Attempts to resolve this bug by specifying a fetchPolicy to 'network-only'
  seems to help, but this needs to be more robustly addressed in future work.

  */
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables: variables, fetchPolicy: 'network-only'});
  let output: React.ReactElement<any> | null;

  if (loading === true) {
    output = null;
  } else if (error !== undefined) {

    console.error(error);
    output = null;

  } else if (data !== undefined) {

    const {location, countryLookback} = data;

    if (location === null || countryLookback === null) {
      output = null;


    } else {

      const {
        largestContributingExportProduct: unfilteredProducts,
      } = countryLookback;
      const otherCategoryID = generateStringProductId({productClass: ProductClass.HS, id: 10});
      // Exclude "Other" product:
      const filteredProducts = unfilteredProducts.filter(item => item.topLevelParent.id !== otherCategoryID);
      const sectorOne = filteredProducts[0].id;
      const sectorTwo = filteredProducts[1].id;
      output = (
        <ExportGrowthDynamicsText
          variables={{
            sectorOne, sectorTwo, year,
          }}
          coreData={data}
          fluentBundle={fluentBundle}
        />
      );
    }
  } else {

    output = null;
  }

  return (
    <Slide>
      <SlideAdvancer
        onClick={onPrevSlideClick}
        direction={Direction.Previous}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-export-complexity', {year: year.toString()})}
      </SlideAdvancer>
      <ContentContainer>
        <Title>{getFluentString('section-nav-slide-export-growth-dynamics')}</Title>
        {output}
      </ContentContainer>
      <SlideAdvancer
        onClick={onNextSlideClick}
        direction={Direction.Next}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-market-share')}
      </SlideAdvancer>
    </Slide>
  );
};
